import React, { useCallback, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
    TextInput,
} from 'grommet';
import {
    API,
    ConfirmDeleteButton,
    RowsPerPageSelect,
    Utils,
    resolveSortField,
    usePaginatedFetch,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';

const SEARCH_DEBOUNCE_MS = 400;

const SORT_FIELD_MAP = {
    name: 'name',
    createdAt: 'createdAt',
};

export const ListsDashboard = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'createdAt', direction: 'desc' },
    });

    const fetchPage = useCallback(
        () => loadLists({ page, pageSize, sort, search: appliedSearchTerm }),
        [page, pageSize, sort, appliedSearchTerm],
    );
    const {
        items: lists,
        status,
        reload,
    } = usePaginatedFetch({
        fetchPage,
        applyPagination,
        transform: Utils.deepFlatten,
    });

    // A new term drops back to page 1, since the current offset may not
    // exist in the narrowed set.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    // Only the list is removed; Strapi drops the contact join rows, so the
    // students themselves remain.
    const deleteList = async (list) => {
        await API.delete(`/api/contact-lists/${list.id}`);
        reload();
    };

    const dataColumns = [
        {
            header: 'List Name',
            property: 'name',
            render: (list) => (
                <Anchor href={`/lists/${list.id}`} label={list.name} />
            ),
            sortable: true,
        },
        {
            header: 'Students',
            property: 'contacts.count',
            render: (list) => list.contacts?.count ?? 0,
            sortable: false,
        },
        {
            header: 'Created',
            property: 'createdAt',
            render: (list) =>
                list.createdAt
                    ? new Date(list.createdAt).toLocaleDateString()
                    : '',
            sortable: true,
        },
        {
            header: '',
            property: 'delete',
            render: (list) => (
                <ConfirmDeleteButton
                    a11yTitle={`Delete list ${list.name}`}
                    message={
                        `Delete list "${list.name}"? ` +
                        'Students in this list will not be deleted.'
                    }
                    errorMessage="Could not delete this list. Please try again."
                    onConfirm={() => deleteList(list)}
                />
            ),
            sortable: false,
        },
    ];

    const renderTableArea = () => {
        if (status === 'error') {
            return (
                <Box pad="medium" align="center">
                    <Text color="status-critical">Could not load lists</Text>
                </Box>
            );
        }
        if (status === 'loading') return null;
        if (lists.length === 0) {
            return (
                <Box pad="medium" align="center">
                    <Text>No lists found</Text>
                </Box>
            );
        }
        return (
            <DataTable
                primaryKey="id"
                sort={{ ...sort, external: true }}
                onSort={handleSort}
                data={lists}
                columns={dataColumns}
            />
        );
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Heading level="2">Lists</Heading>
                    <Box width="medium">
                        <TextInput
                            a11yTitle="Search lists"
                            placeholder="Search by list name"
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                applySearchTerm(event.target.value);
                            }}
                        />
                    </Box>
                    <Box>{renderTableArea()}</Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const loadLists = async ({ page, pageSize, sort, search } = {}) => {
    const term = search?.trim();

    const sortParam = `${resolveSortField(
        sort,
        SORT_FIELD_MAP,
        'createdAt',
    )}:${sort?.direction ?? 'desc'}`;

    const query = QueryString.stringify(
        {
            fields: ['name', 'createdAt'],
            filters: term ? { name: { $containsi: term } } : undefined,
            populate: {
                contacts: {
                    count: true,
                },
            },
            pagination: { page, pageSize },
            sort: [sortParam],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/contact-lists?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return null;
};
